import clsx from "clsx";
import { Link, useLocation } from "react-router-dom";
import { rememberSide, sidePlacePath, type PartsSide } from "./side.js";

/**
 * The two-segment **Service | Wholesale** control at the top of the Parts
 * sidebars (spec §4.1). One click flips the whole shell: each segment is a
 * real link, so it opens in a new tab and shows in the status bar; the
 * current side carries `aria-current="page"`. The other side's segment
 * links to the place that side was last at in this tab (its list and open
 * order — {@link sidePlacePath}), or to its home list when it has not been
 * shown yet; the click also writes the side to the browser's memory, so
 * the next sign-in or the root address reopens it. The current side's
 * segment is a no-op: it links to the address on screen and a click
 * changes nothing but closes a folded menu, so choosing the side already
 * open never drops the place. Only the Parts roles' sidebars render this
 * — no other role ever sees the word Wholesale.
 */

export interface SideSwitchProps {
  side: PartsSide;
  className?: string;
  /** Called after a segment is chosen, so a collapsed menu can close. */
  onNavigate?: () => void;
}

const SEGMENTS: readonly { side: PartsSide; label: string }[] = [
  { side: "service", label: "Service" },
  { side: "wholesale", label: "Wholesale" },
];

export function SideSwitch({ side, className, onNavigate }: SideSwitchProps) {
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  return (
    <div className={clsx("side-switch", className)} role="group" aria-label="Service or Wholesale">
      {SEGMENTS.map((segment) => {
        const current = segment.side === side;
        return (
          <Link
            key={segment.side}
            to={current ? here : sidePlacePath(segment.side)}
            className="side-switch__segment"
            aria-current={current ? "page" : undefined}
            data-side={segment.side}
            onClick={(event) => {
              if (current) event.preventDefault();
              else rememberSide(segment.side);
              onNavigate?.();
            }}
          >
            {segment.label}
          </Link>
        );
      })}
    </div>
  );
}
